// src/pages/appointment/AppointmentManagement.js
// Página principal para agendamiento de citas con calendario

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import Calendar from '../../components/Calendar';
import AppointmentForm from './AppointmentForm';
import { useAuth } from '../../context/AuthContext';
import { createAppointment, getAppointmentsByMonth, getAppointmentsByDate } from '../../services/appointmentApi';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';

const AppointmentManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Estados para el calendario
  const [currentDate, setCurrentDate] = useState(new Date());
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  
  // Estados para citas
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();
  
  // Estados para el modal de cita
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayAppointments, setDayAppointments] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  // Cargar citas del mes
  const loadAppointments = useCallback(async () => {
    try {
      setLoading(true);
      
      const { ok, status, data } = await getAppointmentsByMonth(year, month);
      
      if (ok && (status === 200 || data?.code === 200)) {
        const responseData = data.data || data.message || [];
        setAppointments(responseData.appointments || responseData);
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error cargando citas';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error cargando citas:', error);
      showAlert('Error inesperado al cargar citas', 'error');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  // Cargar citas al montar el componente y cuando cambien año/mes
  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Manejar cambio de mes
  const handleMonthChange = (newMonth) => {
    setMonth(newMonth);
  };

  // Manejar cambio de año
  const handleYearChange = (newYear) => {
    setYear(newYear);
  };

  // Cargar citas del día al abrir el modal para filtrar horarios disponibles
  useEffect(() => {
    if (!showAppointmentModal || !selectedDate) return;
    let cancelled = false;
    const load = async () => {
      const { ok, data } = await getAppointmentsByDate(selectedDate);
      if (cancelled) return;
      if (ok && (data?.code === 200 || data?.data)) {
        const list = data.data || data.message || [];
        setDayAppointments(Array.isArray(list) ? list : list?.appointments || []);
      } else {
        setDayAppointments([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [showAppointmentModal, selectedDate]);

  // Manejar clic en fecha del calendario
  const handleDateClick = (dateStr, day) => {
    setSelectedDate(dateStr);
    setSelectedDay(day);
    setShowAppointmentModal(true);
  };

  // Cerrar modal de cita
  const handleCloseAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedDate(null);
    setSelectedDay(null);
  };

  // Manejar creación de cita
  const handleCreateAppointment = async (appointmentData) => {
    try {
      setModalLoading(true);
      console.log('appointmentData', appointmentData);
      const { ok, status, data } = await createAppointment(appointmentData);
      
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert(data?.message || 'Cita agendada exitosamente', 'success');
        setShowAppointmentModal(false);
        setSelectedDate(null);
        setSelectedDay(null);
        // Recargar citas del mes
        loadAppointments();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error agendando cita';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error agendando cita:', error);
      showAlert('Error inesperado al agendar cita', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Formatear fecha para mostrar
  const formatSelectedDate = () => {
    if (!selectedDate) return '';
    
    const date = new Date(selectedDate);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    
    return date.toLocaleDateString('es-ES', options);
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />

      <Container fluid className="mt-3">
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              <i className="fas fa-calendar-alt me-2"></i>
              Calendario de Citas
            </h4>
            <div className="d-flex gap-2">
              <Button
                variant="outline-info"
                onClick={() => navigate('/gestion-notificaciones')}
              >
                <i className="fas fa-comments me-2"></i>
                Notificaciones WhatsApp
              </Button>
              <Button
                variant="outline-warning"
                onClick={() => navigate('/recordatorios-bano')}
              >
                <i className="fas fa-bell me-2"></i>
                Recordatorios de Baño
              </Button>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            {loading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" size="lg" />
              </div>
            ) : (
              <Calendar
                year={year}
                month={month}
                appointments={appointments}
                onDateClick={handleDateClick}
                onMonthChange={handleMonthChange}
                onYearChange={handleYearChange}
              />
            )}
          </Col>
        </Row>

        {/* Modal de agendamiento de cita */}
        <Modal show={showAppointmentModal} onHide={handleCloseAppointmentModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Agendar Cita - {formatSelectedDate()}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AppointmentForm
              onSubmit={handleCreateAppointment}
              loading={modalLoading}
              selectedDate={selectedDate}
              appointmentsForDate={dayAppointments}
              submitText="Agendar Cita"
              onAlert={showAlert}
            />
          </Modal.Body>
        </Modal>

        {/* Modal de Alerta Temporal */}
        <AlertModal
          show={showAlertModal}
          onHide={hideAlert}
          message={alertMessage}
          type={alertType}
        />
      </Container>
    </>
  );
};

export default AppointmentManagement;
